import Env from '../util/env';
import {serviceURL} from '../util/uiconstants';
import queryString from 'query-string';
import _ from 'lodash';
import {API_RESOURCES, getUrl} from '../util/api';

/**
 * Returns the URL of the GET ALL Controls endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Controls endpoint.
 */
export const GetAllControlsUrl = (engagementId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Controls/${searchString}`;
	return url;
};

/**
 * Returns a query string with all the valid parameters sent as properties of the function's argument object.
 * @param {Object} queryStringParameters The object containing the parameters for the query string.
 * @returns {String} Formatted queryStringParameters.
 */
export const getQueryString = (queryStringParameters) => {
	queryStringParameters = _.omitBy(queryStringParameters, _.isNil);
	return !_.isEmpty(queryStringParameters) ? `?${queryString.stringify(queryStringParameters)}` : '';
};

/**
 * Returns an action object with the specified parameters that can be treated by the api-middleware, ultimately making an API call through Axios.
 * @param {String} type The type of the action.
 * @param {String} url The URL of the api to be called by Axios.
 * @param {String} method The HTTP method (GET, POST, PUT, DELETE, PATCH, OPTIONS, etc.).
 * @param {Object} [data] The payload for POST, PUT, and PATCH requests.
 * @param {String} [trackWithArea] Area to track the promise with.
 * @param {Object} [additionalProps] Additional props.
 * @param {Object} [httpHeaders] HTTP headers to be sent in the requuest.
 * @returns {Object} An action object that can be treated by the api-middleware, ultimately making an API call through Axios.
 */
export const createApiAction = (type, url, method, data, trackWithArea, additionalProps, httpHeaders) => {
	const headers = httpHeaders ? {headers: httpHeaders} : undefined;
	return {
		type,
		meta: {
			api: {
				url,
				method,
				data,
				trackWithArea,
				...headers
			}
		},
		...additionalProps
	};
};

/**
 * Returns the URL of the GET ALL Engagement Users endpoint, for the specified Engagement ID, and with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL EngagementUsers endpoint.
 */
export const getAllEngagementUsersUrl = (engagementId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Users/${searchString}`;

	return url;
};

/**
 * Returns the URL of the GET ALL Form Users endpoint, for the specified Engagement ID and Document ID, and with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {String} documentId The Document ID.
 * @returns {String} Url for GET ALL Form Users endpoint.
 */
export const getAllFormUsersUrl = (engagementId, documentId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/Users`;

	return url;
};

/**
 * Returns the URL of the form BodyRelatedObjects endpoint, for the specified engagementId, documentId and bodyId.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} documentId Document ID for the form.
 * @param {Number} bodyId The Body ID.
 * @returns {String} Url for form body related objects endpoint.
 */
export function getBodyRelatedObjectsUrl(engagementId, documentId, bodyId) {
	return `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/Bodies/${bodyId}/RelatedObjects`;
}

/**
 * Returns the URL of the GET ALL Related Objects endpoint, for the specified Engagement ID and Document ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} documentId Document ID for the form.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Related Objects endpoint.
 */
export const getRelatedObjectsUrl = (engagementId, documentId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/RelatedObjects${searchString}`;

	return url;
};

/**
 * Returns the URL of the GET ALL Scots endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Scots endpoint.
 */
export const GetAllScotsUrl = (engagementId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Scots/${searchString}`;

	return url;
};

/**
 * Returns the URL of the GET ALL IT Applications endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL IT Applications endpoint.
 */
export const GetAllItsoApplicationsUrl = (engagementId, queryStringParameters) => {
	const url = `${getUrl(
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ITAPPLICATIONS]: undefined
		},
		queryStringParameters
	)}`;

	return url;
};

/**
 * Returns the URL of the GET ALL IT Applications endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL IT Applications endpoint.
 */
export const GetITApplicationMetricsUrl = (engagementId, queryStringParameters) => {
	const url = `${getUrl(
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ITAPPLICATIONMETRICS]: undefined
		},
		queryStringParameters
	)}`;

	return url;
};

/**
 * Returns the URL of the GET BY ID IT Applications endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} applicationId The Application ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET BY ID IT Applications endpoint.
 */
export const GetByIdItsoApplicationsUrl = (engagementId, applicationId, queryStringParameters) => {
	const url = `${getUrl(
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ITAPPLICATIONS]: applicationId
		},
		queryStringParameters
	)}`;

	return url;
};

/**
 * Returns the URL of the GET ALL IT Processes endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL IT Processes endpoint.
 */
export const GetAllItProcessUrl = (engagementId, queryStringParameters) => {
	const url = `${getUrl(
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ITPROCESSES]: undefined
		},
		queryStringParameters
	)}`;

	return url;
};

/**
 * Returns the URL of the GET ALL Engagement Comments endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Engagement Comments endpoint.
 */
export const GetAllEngagementCommentsUrl = (engagementId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Comments/${searchString}`;

	return url;
};

/**
 * Returns the URL of the GET ALL Form Comments endpoint, for the specified Engagement ID and Document ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {String} documentId The Document ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Form Comments endpoint.
 */
export const GetAllFormCommentsUrl = (engagementId, documentId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/Comments/${searchString}`;

	return url;
};

/**
 * Returns the URL of the POST Form Comments endpoint, for the specified Engagement ID and Document ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {String} documentId The Document ID.
 * @returns {String} Url for POST Form Comments endpoint.
 */
export const CreateFormCommentsUrl = (engagementId, documentId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/Comments`;

	return url;
};

/**
 * Returns the URL of the PATCH/DELETE Form Comments endpoint, for the specified Engagement ID and Document ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {String} documentId The Document ID.
 * @param {Number} commentId The Comment ID.
 * @returns {String} Url for PATCH/DELETE Form Comments endpoint.
 */
export const PatchDeleteFormCommentsUrl = (engagementId, documentId, commentId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/Comments/${commentId}`;

	return url;
};

/**
 * Returns the URL of the GET BY ID Engagement Internal User endpoint, for the specified Engagement ID and User ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {String} userId The Document ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET BY ID Engagement Internal User endpoint.
 */
export const GetByIdInternalUserUrl = (engagementId, userId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}InternalUsers/${userId}${searchString}`;

	return url;
};

/**
 * Returns the URL of the GET ALL Tasks endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Tasks endpoint.
 */
export const GetAllTasksUrl = (engagementId, queryStringParameters, filterTaskIds = []) => {
	let searchString = getQueryString(queryStringParameters);

	if (filterTaskIds.length > 0) {
		for (let i = 0; i < filterTaskIds.length; i++) {
			searchString = searchString + '&filterTaskID=' + filterTaskIds[i];
		}
	}

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Tasks/${searchString}`;

	return url;
};

/**
 * Returns the URL of the Tasks GetById endpoint, for the specified Engagement ID and Task ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} taskId The Task ID.
 * @param {string} queryString The query string.
 * @returns {String} Url for Tasks GetById endpoint.
 */
export const GetTaskByIdUrl = (engagementId, taskId, queryString) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Tasks/${taskId}${queryString ? `${queryString}` : ''}`;

	return url;
};

export const GetAssignmentsByTasksIdUrl = (engagementId, taskId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}Tasks/${taskId}/Assignments?assignedTo=TASKASSIGNMENTS`;
};

export function GetAllDocumentsUrl(engagementId, queryStringParameters) {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Documents/${searchString}`;

	return url;
}

export function GetFormByIdUrl(engagementId, documentId, queryStringParameters) {
	const searchString = getQueryString(queryStringParameters);

	return `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}${searchString}`;
}

export function GetFormValidationsUrl(engagementId, documentId, queryStringParameters) {
	const _queryStringParameters = {
		...queryStringParameters,
		hasIncompleteResponse: true
	};
	const searchString = getQueryString(_queryStringParameters);

	return `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/Validations${searchString}`;
}

export function GetAllValidationsUrl(engagementId, queryStringParameters) {
	const _queryStringParameters = {
		...queryStringParameters,
		hasIncompleteResponse: true
	};
	const searchString = getQueryString(_queryStringParameters);

	return `${Env.getBaseURL(serviceURL, engagementId)}Validations${searchString}`;
}

export function GetAllValidationsUrlPOST(engagementId, queryStringParameters) {
	const searchString = getQueryString(queryStringParameters);
	return `${Env.getBaseURL(serviceURL, engagementId)}Validations${searchString}`;
}

/**
 * Returns the URL of the PATCH Risk Factor Relation endpoint, for the specified Engagement ID, Engagement Risk Factor ID, and Risk Factor Relation ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} engagementRiskFactorId The Engagement Risk Factor ID.
 * @param {Number} riskFactorRelationId The Risk Factor Relation ID.
 * @returns {String} Url for PATCH Risk Factor Relation endpoint.
 */
export const PatchRiskFactorRelationUrl = (engagementId, engagementRiskFactorId, riskFactorRelationId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}RiskFactors/${engagementRiskFactorId}/Relation/${riskFactorRelationId}`;

	return url;
};

export const PostRiskFactorRelationUrl = (engagementId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}RiskFactors`;

	return url;
};

/**
 * Returns the URL of the POST Form Body References endpoint, for the specified Engagement ID, Document ID and Body ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {String} documentId The Document ID.
 * @param {Number} bodyId The Body ID.
 * @returns {String} Url for POST Form Comments endpoint.
 */
export const PostFormBodyReferenceUrl = (engagementId, documentId, bodyId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/Bodies/${bodyId}/References`;
};

/**
 * Returns the URL for the Annotations endpoint, for the specified engagementId.
 * @param {Number} engagementId The Engagement ID.
 * @returns {String} Url for annotations endpoint.
 */
export function getAnnotationsUrl(engagementId) {
	return `${Env.getBaseURL(serviceURL, engagementId)}Annotations/`;
}

/**
 * Returns the URL of the GET ALL Annotations endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Annotations endpoint.
 */
export const GetAllAnnotationsUrl = (engagementId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Annotations/${searchString}`;

	return url;
};

/**
 * Returns the URL of the PATCH Annotations endpoint, for the specified Engagement ID.
 * @param {Number} engagementId The Engagement ID.
 * @returns {String} Url for PATCH Annotations endpoint.
 */
export const patchAnnotationUrl = (engagementId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Annotations`;

	return url;
};

/**
 * Returns the URL of the DELETE Annotations endpoint, for the specified Engagement ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} annotationId The Annotation ID.
 * @returns {String} Url for DELETE Annotations endpoint.
 */
export const deleteAnnotationUrl = (engagementId, annotationId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Annotations/${annotationId}`;

	return url;
};

/**
 * Returns the URL of the GET ALL Materiality endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Materiality endpoint.
 */
export const GetAllMaterialityUrl = (engagementId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Materiality${searchString}`;

	return url;
};

/**
 * Returns the URL of the UPDATE/DELETE Materiality endpoint, for the specified Engagement ID and Materiality ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} materialityId The Materiality ID.
 * @returns {String} Url for UPDATE/DELETE Materiality endpoint.
 */
export const UpdateMaterialityByIdUrl = (engagementId, materialityId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}Materiality/${materialityId}`;
};

/**
 * Returns the URL of the UPDATE/DELETE Materiality endpoint, for the specified Engagement ID and Materiality ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} materialityId The Materiality ID.
 * @returns {String} Url for UPDATE/DELETE Materiality endpoint.
 */
export const UpdateDeleteMaterialityUrl = (engagementId, materialityId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}Materiality/${materialityId}`;
};

/**
 * Returns the URL of the Create Materiality endpoint, for the specified Engagement ID.
 * @param {Number} engagementId The Engagement ID.
 * @returns {String} Url for Create Materiality endpoint.
 */
export const CreateMaterialityUrl = (engagementId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}Materiality/`;
};

/**
 * Returns the URL of the DELETE Document endpoint, for the specified Engagement ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} documentId The Document ID.
 * @returns {String} Url for DELETE Document endpoint.
 */
export const deleteDocumentUrl = (engagementId, documentId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Documents/${documentId}`;

	return url;
};

/**
 * Returns the URL of the GET BY ID IT Application/SO endpoint, for the specified Engagement ID with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} entityUId the entity uid.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET BY ID Engagement Internal User endpoint.
 */
export const GetByIdITAppSoUrl = (engagementId, entityUId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}ITApplications/${entityUId}${searchString}`;

	return url;
};

/* * Returns the URL of the POST submit independence template endpoint, for the specified Engagement ID and Document ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} documentId The Document ID.
 * @returns {String} Url for POST submit independence template.
 */
export const submitIndependenceTemplateUrl = (engagementId, documentId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/SubmitIndependenceTemplate`;

	return url;
};

/**
 * Returns the URL of the POST create independence template snapshot endpoint, for the specified Engagement ID and Document ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} documentId The Document ID.
 * @returns {String} Url for POST create independence template snapshot.
 */
export const createIndependenceSnapshotUrl = (engagementId, documentId) => {
	return Env.getBaseURL(serviceURL, engagementId) + 'Forms/' + documentId + '/IndependenceSnapshot';
};

/**
 * Returns the URL of the GET ALL Journal Source endpoint, for the specified Engagement ID, and with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Journal Source endpoint.
 */
export const getAllJournalSourceUrl = (engagementId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}JournalSource/${searchString}`;

	return url;
};

/**
 * Returns the URL of the Update Journal Source, for the specified JournalSourceId.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} journalSourceId The Journal Source Id.
 * @returns {String} Url for Update Journal Source.
 */
export const updateJournalSourceUrl = (engagementId, journalSourceId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}JournalSource/${journalSourceId}`;

	return url;
};

/**
 * Returns the URL of the GET ALL Estimates endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GET ALL Materiality endpoint.
 */
export const GetAllEstimatesUrl = (engagementId, queryStringParameters) => {
	const searchString = getQueryString(queryStringParameters);

	const url = `${Env.getBaseURL(serviceURL, engagementId)}Estimates/${searchString}`;

	return url;
};

export const postSCOTJournalSourceUrl = (engagementId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Scots/SCOTJournalSource`;
	return url;
};

export const deleteSCOTJournalSourceUrl = (engagementId, SCOTJournalSourceId) => {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Scots/SCOTJournalSource/${SCOTJournalSourceId}`;
	return url;
};

//Group Audit

/**
 * Returns the URL of the GET ALL group scopes endpoint.
 * @param {Number} engagementId Engagement ID.
 * @returns {String} Url for GET ALL scopes.
 */
export const getAllScopesUrl = (engagementId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}` + API_RESOURCES.SCOPES;
};

/**
 * Returns the URL of the GET a component by id endpoint.
 * @param {Number} engagementId Engagement ID.
 * * @param {Number} componentId Component ID.
 * @param {String} includeGroupRiskAssessment includeGroupRiskAssessment value.
 * @returns {String} Url for GET component by id.
 */
export const getComponentByIdUrl = (engagementId, componentId, includeGroupRiskAssessment) => {
	return `${Env.getBaseURL(serviceURL, engagementId, componentId)}${
		API_RESOURCES.COMPONENTS
	}/${componentId}?includeGroupRiskAssessment=${includeGroupRiskAssessment}`;
};

/**
 * Returns the URL of the GET ALL group accounts endpoint.
 * @param {Number} engagementId Engagement ID.
 * @returns {String} Url for GET ALL components.
 */
export const getAllAccountsUrl = (engagementId, {searchString = '', page = 1, pageSize = 20}) => {
	const includes = 'includeAssertions=true&includePspIndexes=true&includeRelatedComponents=true';
	const filterSignificantAccount = '&filterAccountTypes=significantAccount';
	const filterLimitedRiskAccount = '&filterAccountTypes=limitedRiskAccount';
	const filterSignificantDisclosure = '&filterAccountTypes=significantDisclosure';
	const searchStringParameter = `&searchString=${searchString}`;
	const paginator = `&includePagination=true&page=${page}&pageSize=${pageSize}`;
	const groupExecutedInComponentEngagementOnly = '&filterExecutionTypes=groupExecutedInComponentEngagementOnly';
	const groupExecutedInPrimaryAndComponentEngagement = '&filterExecutionTypes=groupExecutedInPrimaryAndComponentEngagement';
	return `${Env.getBaseURL(serviceURL, engagementId)}${
		API_RESOURCES.ACCOUNTS
	}?${includes}${filterSignificantAccount}${filterLimitedRiskAccount}${filterSignificantDisclosure}${searchStringParameter}${paginator}${groupExecutedInComponentEngagementOnly}${groupExecutedInPrimaryAndComponentEngagement}`; // TODO: make the constant
};

/**
 * Returns the URL of the ADD/REMOVE endpoints for association between gacomponents and account.
 * @param {Number} engagementId Engagement ID.
 * @returns {String} Url of the ADD/REMOVE endpoints for association between gacomponents and account.
 */
export const gaComponentsAccountURL = (engagementId, accountId, isDelete = false, componentId) => {
	const url = [`${Env.getBaseURL(serviceURL, engagementId)}`, API_RESOURCES.ACCOUNTS, `/${accountId}`];

	if (isDelete) url.push(`/GAComponent/${componentId}`);
	else url.push('/Component');

	return url.join('');
};

/**
 * Returns the URL of the Create Materiality endpoint, for the specified Engagement ID.
 * @param {Number} engagementId The Engagement ID.
 * @returns {String} Url for Create Component endpoint.
 */
export const GroupAuditComponentUrl = (engagementId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.COMPONENTS}`;
};

export const CreateSendLinkRequestUrl = (engagementId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.GROUPLINKS}`;
};

/**
 * Returns the URL of the GET ALL group instructions endpoint.
 * @param {Number} engagementId Engagement ID.
 * @returns {String} Url for GET ALL instructions.
 */
export const getAllInstructionsUrl = (engagementId) => {
	// TODO: make the constant
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.INSTRUCTIONS}`;
};

/**
 * Returns the URL of the Group Snapshots endpoint.
 * @param {Number} engagementId Engagement ID.
 * @param {Boolean} isScopeSnapshot Query string parameter to create Scopes or Components Snapshots.
 * @returns {String} Url for Group Snapshots endpoint.
 */
export const getGroupSnapshotUrl = (engagementId, isScopeSnapshot) => {
	// TODO: make the constant
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.GROUPSNAPSHOTS}?isScopeSnapshot=${isScopeSnapshot}`;
};

/**
 * Returns the URL of  PATCH group instructions endpoint.
 * @param {Number} engagementId Engagement ID.
 * @param {Number} instructionId instruction Id.
 * @returns {String} Url for GET ALL instructions.
 */
export const patchGroupInstructionsUrl = (engagementId, instructionId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.INSTRUCTIONS}/${instructionId}`;
};

/**
 * Returns the URL of DELETE instruction endpoint.
 * @param {Number} engagementId is the Engagement ID.
 * @param {Number} instructionId is the Instruction Id.
 * @returns {String} Url for DELETE an instruction.
 */
export const deleteInstructionUrl = (engagementId, instructionId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.INSTRUCTIONS}/${instructionId}`;
};

/** Returns the URL of the GroupSections endpoint, for the specified Engagement ID.
 * @param {Number} engagementId The Engagement ID.
 * @returns {String} Url for GroupSections endpoint.
 */
export const GroupSectionsUrl = (engagementId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.GROUPSECTIONS}`;
};

/**
 * Returns the URL of DELETE instruction endpoint.
 * @param {Number} engagementId is the Engagement ID.
 * @param {Number} sectionId is the Section Id.
 * @returns {String} Url for DELETE a Section.
 */
export const deleteSectionUrl = (engagementId, sectionId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.GROUPSECTIONS}/${sectionId}`;
};

/** Returns the URL of the FormActions endpoint, for the specified Engagement ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for GroupSections endpoint.
 */
export const GetAllFormActionsBaseUrl = (engagementId, queryStringParameters) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.FORMACTIONS}${getQueryString(queryStringParameters)}`;
};

/** Returns the URL of the FormActions endpoint, for the specified Engagement ID.
 * @param {Number} engagementId The Engagement ID.
 * @param {String} documentIds Document IDs to filter.
 * @param {Number} formCategory The Form category enum.
 * @param {Boolean} includeAvailableActions If includes availabe actions.
 * @param {Boolean} includeSelectedActions If includes selected actions.
 * @returns {String} Url for GroupSections endpoint.
 */
export const getAllFormActionsUrl = (engagementId, documentIds, formCategory, includeAvailableActions, includeSelectedActions) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.FORMACTIONS}
		?formCategory=${formCategory}
		&filterDocumentIds=${documentIds}
		&includeAvailableActions=${includeAvailableActions}
		&includeSelectedActions=${includeSelectedActions}`;
};

/** Deletes relation between task and docmuent.
 * @param {Number} engagementId The Engagement ID.
 * @param {String} taskId Document IDs to be delete.
 * @param {String} documentId Task IDs to be delete.
 * @returns {204} No content response.
 */
export const deleteTaskDocumentRelateUrl = (engagementId, taskId, documentId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.TASKS}
		/${taskId}/Documents/${documentId}`;
};
/**
 * Returns the URL of the GroupSnapshots get by Id.
 * @param {Number} engagementId is the Engagement ID.
 * @param {Number} snapshotId is the Snapshot ID.
 * @param {Number} componentId is the Component ID.
 * @param {String} setSnapshotId is the Set Snapshot ID needed for the get by Id.
 * @returns {String} Url for get snapshot by snapshot Id.
 */
export const groupSnapshotUrl = (engagementId, snapshotId, componentId, setSnapshotId) => {
	let searchString = `gAComponentId=${componentId}`;
	searchString += setSnapshotId !== null && setSnapshotId !== undefined ? `&setSnapshotId=${setSnapshotId}` : '';
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.GROUPSNAPSHOTS}/${snapshotId}?${searchString}`;
};
/**
 * Returns the URL of the GroupSnapshots get all.
 * @param {Number} engagementId is the Engagement ID.
 * @param {Number} scopeId is the Scope ID.
 * @param {Boolean} isSentVersion is the isSentVersion prop.
 * @returns {String} Url for get snapshot by snapshot Id.
 */
export const allGroupSnapshotsUrl = (engagementId, scopeId, isSentVersion) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}
			${API_RESOURCES.GROUPSNAPSHOTS}?scopeId=${scopeId}&isSentVersion=${isSentVersion}`;
};

/**
 * Returns the URL of the Group Instructions Risk Assement Packages.
 * @param {Number} engagementId is the Engagement ID.
 * @param {Number} instructionId is the Instruction ID.
 * @returns {String} Url to POST and ALRA Package.
 */
export const groupInstructinALRAPackageUrl = (engagementId, instructionId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.INSTRUCTIONS}/${instructionId}/${API_RESOURCES.RISKASSESSMENTPACKAGE}`;
};

/**
 * Returns the URL of the Group Tasks Involvement Packages.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} taskId The Task ID.
 * @returns {String} Url for Create Component endpoint.
 */
export const GroupTasksUrl = (engagementId, taskId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.GROUPTASKS}/${taskId}/${API_RESOURCES.INVOLVEMENTPACKAGES}`;
};

/**
 * Returns the URL of DELETE subscope endpoint.
 * @param {Number} engagementId is the Engagement ID.
 * @param {Number} scopeId is the scope Id.
 * @returns {String} Url for DELETE a subscope.
 */
export const deleteSubScopeUrl = (engagementId, scopeId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}${API_RESOURCES.SCOPES}/${scopeId}`;
};

/**
 * Returns the URL of the EDIT group sub scope endpoint.
 * @param {Number} engagementId Engagement ID.
 * @param {Number} scopeId Scope ID.
 * @returns {String} Url for GET ALL scopes.
 */
export const editSubScopesUrl = (engagementId, scopeId) => {
	return `${Env.getBaseURL(serviceURL, engagementId)}` + API_RESOURCES.SCOPES + `/${scopeId}`;
};

/**
 * Returns the URL of the Get FormBodies endpoint, for the specified Engagement ID, with the specified query string parameters.
 * @param {Number} engagementId The Engagement ID.
 * @param {Object} [queryStringParameters] An object containing the parameters of the query string as properties.
 * @returns {String} Url for Get FormBodies endpoint.
 */
export const getFormBodiesUrl = (engagementId, queryStringParameters) => {
	const url = `${getUrl(
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.FORMBODIES]: undefined
		},
		queryStringParameters
	)}`;

	return url;
};
